import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSavedViews,
  createSavedView,
  updateSavedView,
  deleteSavedView,
} from '../api';
import type { CreateSavedViewInput, UpdateSavedViewInput } from '../types';

// Query key factory
export const savedViewsKeys = {
  all: ['saved-views'] as const,
  list: (feature: string) => [...savedViewsKeys.all, feature] as const,
};

/**
 * Hook to fetch saved views for a feature
 */
export function useSavedViews(feature: string) {
  return useQuery({
    queryKey: savedViewsKeys.list(feature),
    queryFn: () => getSavedViews(feature),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!feature,
    // Don't retry on auth errors - prevents redirect loops
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status: number }).status;
        if (status === 401 || status === 403) return false;
      }
      return failureCount < 3;
    },
    // Return empty array on error so the page can still function
    placeholderData: [],
  });
}

/**
 * Hook to create a new saved view
 */
export function useCreateSavedView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSavedViewInput) => createSavedView(data),
    onSuccess: (_, variables) => {
      // Invalidate the list for this feature
      queryClient.invalidateQueries({ queryKey: savedViewsKeys.list(variables.feature) });
    },
  });
}

/**
 * Hook to update a saved view
 */
export function useUpdateSavedView(feature: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSavedViewInput }) =>
      updateSavedView(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedViewsKeys.list(feature) });
    },
  });
}

/**
 * Hook to delete a saved view
 */
export function useDeleteSavedView(feature: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteSavedView(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedViewsKeys.list(feature) });
    },
  });
}
